import React, { createContext, useCallback, useContext, useRef, useState } from 'react'

interface TabRegistration {
  save: () => Promise<void>
  dirty: boolean
}

interface CampaignEditContextValue {
  registerSave: (tabId: string, saveFn: () => Promise<void>) => void
  unregisterSave: (tabId: string) => void
  markDirty: (tabId: string) => void
  isDirty: boolean
  isSaving: boolean
  saveAll: () => Promise<void>
}

const CampaignEditContext = createContext<CampaignEditContextValue | null>(null)

export const CampaignEditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tabsRef = useRef<Map<string, TabRegistration>>(new Map())
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const updateDirtyState = useCallback(() => {
    const anyDirty = Array.from(tabsRef.current.values()).some(t => t.dirty)
    setIsDirty(anyDirty)
  }, [])

  const registerSave = useCallback(
    (tabId: string, saveFn: () => Promise<void>) => {
      const existing = tabsRef.current.get(tabId)
      tabsRef.current.set(tabId, { save: saveFn, dirty: existing?.dirty ?? false })
    },
    []
  )

  const unregisterSave = useCallback(
    (tabId: string) => {
      tabsRef.current.delete(tabId)
      updateDirtyState()
    },
    [updateDirtyState]
  )

  const markDirty = useCallback(
    (tabId: string) => {
      const tab = tabsRef.current.get(tabId)
      if (tab) {
        tab.dirty = true
        updateDirtyState()
      }
    },
    [updateDirtyState]
  )

  const saveAll = useCallback(async () => {
    setIsSaving(true)
    const dirtyTabs = Array.from(tabsRef.current.entries()).filter(([, t]) => t.dirty)
    for (const [, tab] of dirtyTabs) {
      await tab.save()
    }
    for (const [, tab] of tabsRef.current.entries()) {
      tab.dirty = false
    }
    setIsDirty(false)
    setIsSaving(false)
  }, [])

  return <CampaignEditContext.Provider value={{ registerSave, unregisterSave, markDirty, isDirty, isSaving, saveAll }}>{children}</CampaignEditContext.Provider>
}

export const useCampaignEdit = () => {
  const context = useContext(CampaignEditContext)
  if (!context) {
    throw new Error('useCampaignEdit must be used within CampaignEditProvider')
  }
  return context
}
